import React, { SyntheticEvent, useEffect, useRef, useState } from 'react';

import { Money, UuidResult } from '../../../../../api-client';
import {
  Button,
  CheckIcon,
  CustomLink,
  InputErrors,
  InputMoney,
  Loader,
  Modal,
  PrimaryButton,
} from '../../../../../components';
import { PrimaryButtonColor } from '../../../../../components/common/buttons/decorators/PrimaryButton/PrimaryButton';
import { WithdrawFAQ } from '../../../../../components/pages';
import { createWithdrawManager } from '../../../../../di';
import { useAppDispatch, useErrorCallback, useProfileData } from '../../../../../hooks';
import { subtractBalance } from '../../../../../store/profile';
import { Errors, RouteDictionary } from '../../../../../utils';
import styles from './Withdraw.module.scss';

type WithdrawErrors = {
  minAmount?: Errors;
  maxAmount?: Errors;
};

const InfoIcon = () => (
  <svg
    className={styles.infoIcon}
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
    <path d="M12 11V16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="12" cy="8" r="1" fill="currentColor" />
  </svg>
);

const Withdraw = () => {
  const [money, setMoney] = useState<Money>(new Money(100000, 'RUB'));
  const [result, setResult] = useState<UuidResult | null>(null);
  const [loading, setLoading] = useState(false);
  const handleError = useErrorCallback();
  const focused = useRef<boolean>(false);
  const { balance } = useProfileData();
  const [errors, setErrors] = useState<WithdrawErrors>({});
  const minSum = 100000;
  const dispatch = useAppDispatch();

  const onSubmit = async (event: SyntheticEvent) => {
    event.preventDefault();
    setLoading(true);
    try {
      const manager = await createWithdrawManager();
      const result = await manager.create(money);
      setResult(result);
      dispatch(subtractBalance(money.amount));
    } catch (error: any) {
      await handleError(error);
    } finally {
      setLoading(false);
    }
  };

  const validateBlock = (): boolean => {
    const errors: WithdrawErrors = {};

    if (money.amount < minSum) {
      errors.minAmount = [`Минимальная сумма: ${minSum / 100} ₽`];
    }
    if (money.amount > balance.amount) {
      errors.maxAmount = ['Недостаточно средств на счете'];
    }
    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  useEffect(() => {
    validateBlock();
  }, [money, balance]);

  return (
    <div className={styles.withdraw}>
      <nav className={styles.breadcrumbs}>
        <CustomLink to={RouteDictionary.MY_MONEY} className={styles.breadcrumbLink}>
          Главная
        </CustomLink>
        <span className={styles.breadcrumbSeparator}>/</span>
        <span className={styles.breadcrumbCurrent}>Вывод средств</span>
      </nav>

      <div className={styles.grid}>
        <section className={styles.card}>
          <h2 className={styles.cardTitle}>Вывод средств</h2>

          <form className={styles.form} onSubmit={onSubmit}>
            <div className={styles.fieldLabel}>Введите сумму для вывода</div>

            <div className={styles.inputRow}>
              <div className={styles.inputWrap}>
                <InputMoney
                  money={money}
                  onValueChanged={setMoney}
                  placeholder="1000"
                  onFocus={() => (focused.current = true)}
                  onBlur={() => (focused.current = false)}
                />
              </div>
              <div className={styles.submit}>
                {loading ? (
                  <Loader />
                ) : (
                  <PrimaryButton expanded large color={PrimaryButtonColor.GREEN}>
                    <Button type="submit" disabled={Object.keys(errors).length !== 0}>
                      Вывести
                    </Button>
                  </PrimaryButton>
                )}
              </div>
            </div>

            <div className={styles.hints}>
              {!errors.minAmount && (
                <div className={styles.minHint}>{`Минимальная сумма: ${minSum / 100} ₽`}</div>
              )}
              {errors.minAmount && <InputErrors errors={errors.minAmount} />}
              {errors.maxAmount && <InputErrors errors={errors.maxAmount} />}
            </div>

            <div className={styles.notes}>
              <div className={styles.note}>
                <InfoIcon />
                <span>
                  Выведение средств осуществляется на банковский расчётный счёт, с которого вы
                  делали зачисление на номинальный счет.
                </span>
              </div>
              <div className={styles.note}>
                <InfoIcon />
                <span>
                  Оператор платформы передает в автоматическом режиме распоряжение в банк на
                  выведение средств в срок не более 2 дней с момента получения запроса.
                </span>
              </div>
            </div>
          </form>
        </section>

        <section className={styles.card}>
          <WithdrawFAQ />
        </section>
      </div>

      <Modal active={result !== null} onClose={() => setResult(null)} className={styles.modal}>
        <div className={styles.modalBody}>
          <CheckIcon preset="circle-success" size={56} />
          <div className={styles.modalTitle}>Запрос отправлен</div>
          <div className={styles.modalText}>
            Мы получили ваш запрос. Ожидайте поступления средств в течение 2 дней.
          </div>
          <div className={styles.modalAction}>
            <PrimaryButton expanded large color={PrimaryButtonColor.GREEN}>
              <Button onClick={() => setResult(null)}>Понятно</Button>
            </PrimaryButton>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Withdraw;
